import {Component, inject} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {MatSelectModule} from "@angular/material/select";
import {MatFormFieldModule} from "@angular/material/form-field";
import {FormsModule} from "@angular/forms";
import {ConfirmationComponent} from "../../components/confirmation/confirmation.component";
import {TableSort} from "../../generated-sources/api/user-setting";

export interface TableSettingsDialogData {
    tableKey: string;
    columnGroups: Array<{ label: string; cols: string[] }>;
    columnLabels: Record<string, string>;
    currentColumns: string[];
    currentPageSize: number;
    currentSort: TableSort;
    pageSizeOptions: number[];
    sortableColumns: Array<{ value: string; label: string }>;
    isAdmin: boolean;
    hidePageSize?: boolean;
    defaultColumns: string[];
    defaultPageSize: number;
    defaultSort: TableSort;
}

export type TableSettingsDialogResult =
    | {
        columns: string[];
        pageSize: number;
        sort: TableSort;
        saveUser: boolean;
        saveAdmin: boolean;
    }
    | undefined;

@Component({
    selector: "valumize-table-settings-dialog",
    templateUrl: "./table-settings-dialog.component.html",
    styleUrls: ["./table-settings-dialog.component.scss"],
    standalone: true,
    imports: [MatDialogModule, MatCheckboxModule, MatButtonModule, MatIconModule, MatSelectModule, MatFormFieldModule, FormsModule],
})
export class TableSettingsDialogComponent {
    private readonly dialogRef = inject(MatDialogRef<TableSettingsDialogComponent>);
    private readonly data: TableSettingsDialogData = inject(MAT_DIALOG_DATA);
    private readonly dialog = inject(MatDialog);

    readonly columnGroups = this.data.columnGroups;
    readonly columnLabels = this.data.columnLabels;
    readonly pageSizeOptions = this.data.pageSizeOptions;
    readonly sortableColumns = this.data.sortableColumns;
    readonly isAdmin = this.data.isAdmin;
    readonly hidePageSize = this.data.hidePageSize ?? false;
    readonly singleGroup = this.data.columnGroups.length === 1;

    selectedSet = new Set<string>(this.data.currentColumns);
    pageSize = this.data.currentPageSize;
    sortField = this.data.currentSort.field;
    sortDirection = this.data.currentSort.direction;

    saveAsDefault = false;
    saveAsAdmin = false;

    get totalColumnCount(): number {
        return this.data.columnGroups.reduce((sum, g) => sum + g.cols.length, 0);
    }

    get selectedColumnCount(): number {
        return this.selectedSet.size;
    }

    get buttonLabel(): string {
        return this.saveAsDefault || this.saveAsAdmin ? "Save" : "Apply";
    }

    labelFor(col: string): string {
        return this.columnLabels[col] ?? col;
    }

    isSelected(col: string): boolean {
        return this.selectedSet.has(col);
    }

    toggleColumn(col: string): void {
        if (this.selectedSet.has(col)) {
            this.selectedSet.delete(col);
        } else {
            this.selectedSet.add(col);
        }
    }

    groupSelectedCount(group: { cols: string[] }): number {
        return group.cols.reduce((acc, c) => acc + (this.selectedSet.has(c) ? 1 : 0), 0);
    }

    isGroupAllSelected(group: { cols: string[] }): boolean {
        return group.cols.length > 0 && this.groupSelectedCount(group) === group.cols.length;
    }

    isGroupIndeterminate(group: { cols: string[] }): boolean {
        const n = this.groupSelectedCount(group);
        return n > 0 && n < group.cols.length;
    }

    toggleGroup(group: { cols: string[] }, checked: boolean): void {
        if (checked) {
            for (const c of group.cols) this.selectedSet.add(c);
        } else {
            for (const c of group.cols) this.selectedSet.delete(c);
        }
    }

    private buildResult(): { columns: string[]; pageSize: number; sort: TableSort } {
        return {
            columns: this.data.columnGroups.flatMap(g => g.cols).filter(c => this.selectedSet.has(c)),
            pageSize: this.pageSize,
            sort: {field: this.sortField, direction: this.sortDirection}
        };
    }

    apply(): void {
        if (this.saveAsAdmin) {
            const dialogRef = this.dialog.open(ConfirmationComponent);
            dialogRef.componentInstance.confirmMessage = "Set as Company Default?";
            dialogRef.componentInstance.htmlContent =
                "This will update the default table settings for all users. Users who have saved personal settings will keep theirs.";
            dialogRef.afterClosed().subscribe(confirmed => {
                if (confirmed) {
                    this.dialogRef.close({...this.buildResult(), saveUser: this.saveAsDefault, saveAdmin: true});
                }
            });
        } else {
            this.dialogRef.close({...this.buildResult(), saveUser: this.saveAsDefault, saveAdmin: false});
        }
    }

    reset(): void {
        this.selectedSet = new Set<string>(this.data.defaultColumns);
        this.pageSize = this.data.defaultPageSize;
        this.sortField = this.data.defaultSort.field;
        this.sortDirection = this.data.defaultSort.direction;
        this.saveAsDefault = false;
        this.saveAsAdmin = false;
    }

    cancel(): void {
        this.dialogRef.close(undefined);
    }
}
